import { memoize, random } from 'lodash';

const NUMBERS_FOR_DIV_MODES = {
    withoutReturns: 0,
    withOneReturn: 1,
    withTwoReturns: 2,
};

const RULE101_OPERATIONS = ['101', '1001', '10101'];

export default {
    data() {
        return {
            prevMultiplicationExample: {
                left: 0,
                right: 0,
            },
            prevDivisionExample: {
                left: 0,
                right: 0,
            },
            examplesWithAnswerNotTenInTheRowCounter: 0,
        };
    },
    methods: {
        checkDivModes(dividend, divider) {
            try {
                const {
                    divMode,
                    digitCapacity: { left: leftCapacityArray },
                } = this.parameters;
                const leftCapacity = leftCapacityArray[0];

                const dividendAsArray = Math.abs(dividend)
                    .toString()
                    .split('')
                    .map(Number);
                const dividerAsArray = Math.abs(divider)
                    .toString()
                    .split('')
                    .map(Number);

                const arrayToNum = memoize(numArray => Number(numArray.join('')));

                const firstDigitLeft = dividendAsArray[0];
                const firstDigitRight = dividerAsArray[0];
                const secondDigitLeft = dividendAsArray[1];
                const secondDigitRight = dividerAsArray[1];
                const thirdDigitLeft = dividendAsArray[2];
                const firstTwoDigitsLeft = arrayToNum([firstDigitLeft, secondDigitLeft]);

                let result;

                const returnsObject = {
                    [NUMBERS_FOR_DIV_MODES.withoutReturns]: 0,
                    [NUMBERS_FOR_DIV_MODES.withOneReturn]: 0,
                    [NUMBERS_FOR_DIV_MODES.withTwoReturns]: 0,
                };

                const getNeededNumberAfterReturnsAndFillObject = (conditionVariable, conditionFunction) => {
                    let localConditionVariable = conditionVariable;
                    while (conditionFunction(localConditionVariable)) {
                        localConditionVariable--;
                    }
                    const currentReturn = conditionVariable - localConditionVariable;
                    if (currentReturn > NUMBERS_FOR_DIV_MODES[divMode]) {
                        throw new Error('');
                    }
                    returnsObject[currentReturn]++;
                    return localConditionVariable;
                };

                const checkReturnsForDividendDigitCapacityThree = (forNextIteration = false) => {
                    let resultArray, remainingDividend;
                    if (firstDigitLeft > firstDigitRight) {
                        let firstDigitOfPossibleAnswer = Math.floor(firstDigitLeft / firstDigitRight);
                        const firstDigitOfResult = getNeededNumberAfterReturnsAndFillObject(
                            firstDigitOfPossibleAnswer,
                            firstDigitOfPossibleAnswer => {
                                let possibleRemainingDividend =
                                    (firstDigitLeft - firstDigitOfPossibleAnswer * firstDigitRight) * 10 +
                                    secondDigitLeft;
                                return possibleRemainingDividend < firstDigitOfPossibleAnswer * secondDigitRight;
                            }
                        );
                        remainingDividend =
                            (firstDigitLeft - firstDigitOfResult * firstDigitRight) * 10 +
                            secondDigitLeft -
                            firstDigitOfResult * secondDigitRight;
                        let secondDigitOfPossibleResult = Math.floor(remainingDividend / firstDigitRight);
                        let secondDigitOfResult = getNeededNumberAfterReturnsAndFillObject(
                            secondDigitOfPossibleResult,
                            secondDigitOfPossibleResult =>
                                (remainingDividend - secondDigitOfPossibleResult * firstDigitRight) * 10 +
                                    thirdDigitLeft <
                                secondDigitOfPossibleResult * secondDigitRight
                        );
                        resultArray = [firstDigitOfResult, secondDigitOfResult];
                    } else if (firstDigitLeft < firstDigitRight) {
                        const possibleAnswer = Math.floor(firstTwoDigitsLeft / firstDigitRight);
                        resultArray = [
                            getNeededNumberAfterReturnsAndFillObject(possibleAnswer, possibleAnswer => {
                                const possibleRemainingDividend =
                                    (firstTwoDigitsLeft - possibleAnswer * firstDigitRight) * 10 + thirdDigitLeft;
                                return possibleRemainingDividend < possibleAnswer * secondDigitRight;
                            }),
                        ];
                    }
                    return forNextIteration
                        ? {
                              dividendToGetPrevDigit: remainingDividend,
                              dividendToGetPrevDigitForSecondCase: firstTwoDigitsLeft,
                              resultArray,
                          }
                        : arrayToNum(resultArray);
                };

                const findDataForResultAndNextIteration = ({
                    dividendToGetPrevDigit,
                    prevResult,
                    currentDigitLeft,
                    prevDigitLeft,
                    dividendToGetPrevDigitForSecondCase,
                }) => {
                    if (firstDigitLeft > firstDigitRight) {
                        const dividendAfterPreviousDigit =
                            (dividendToGetPrevDigit - prevResult[prevResult.length - 1] * firstDigitRight) * 10 +
                            prevDigitLeft;
                        const dividendToGetNextDigit =
                            dividendAfterPreviousDigit - prevResult[prevResult.length - 1] * secondDigitRight;
                        const possibleDigit = Math.floor(dividendToGetNextDigit / firstDigitRight);
                        const newDigit = getNeededNumberAfterReturnsAndFillObject(possibleDigit, possibleDigit => {
                            return (
                                (dividendToGetNextDigit - possibleDigit * firstDigitRight) * 10 + currentDigitLeft <
                                possibleDigit * secondDigitRight
                            );
                        });
                        return {
                            resultNum: arrayToNum([...prevResult, newDigit]),
                            newDigit,
                            dividendToGetNextDigit,
                        };
                    } else if (firstDigitLeft < firstDigitRight) {
                        const dividendAfterPrevDigitOfResult =
                            (dividendToGetPrevDigitForSecondCase -
                                prevResult[prevResult.length - 1] * firstDigitRight) *
                                10 +
                            prevDigitLeft;
                        const dividendToGetNextDigitOfResult =
                            dividendAfterPrevDigitOfResult - prevResult[prevResult.length - 1] * secondDigitRight;
                        const nextPossibleDigitOfResult = Math.floor(dividendToGetNextDigitOfResult / firstDigitRight);
                        const newDigit = getNeededNumberAfterReturnsAndFillObject(
                            nextPossibleDigitOfResult,
                            nextPossibleDigitOfResult =>
                                (dividendToGetNextDigitOfResult - nextPossibleDigitOfResult * firstDigitRight) * 10 +
                                    currentDigitLeft <
                                nextPossibleDigitOfResult * secondDigitRight
                        );
                        return {
                            resultNum: arrayToNum([...prevResult, newDigit]),
                            newDigit,
                            dividendToGetNextDigitOfResult,
                        };
                    }
                };

                const checkReturnsForSelectedDigitCapacity = (selectedCapacity, forNextIteration = false) => {
                    let { dividendToGetPrevDigit, dividendToGetPrevDigitForSecondCase, resultArray } =
                        selectedCapacity === 4
                            ? checkReturnsForDividendDigitCapacityThree(true)
                            : checkReturnsForSelectedDigitCapacity(selectedCapacity - 1, true);

                    let {
                        resultNum: result,
                        newDigit,
                        dividendToGetNextDigitOfResult,
                        dividendToGetNextDigit,
                    } = findDataForResultAndNextIteration({
                        dividendToGetPrevDigit,
                        prevResult: resultArray,
                        currentDigitLeft: dividendAsArray[selectedCapacity],
                        prevDigitLeft: dividendAsArray[selectedCapacity - 1],
                        dividendToGetPrevDigitForSecondCase,
                    });

                    return forNextIteration
                        ? {
                              resultArray: [...resultArray, newDigit],
                              dividendToGetPrevDigit: dividendToGetNextDigit,
                              dividendToGetPrevDigitForSecondCase: dividendToGetNextDigitOfResult,
                          }
                        : result;
                };

                if (this.parameters.divMode === 'firstDigitsAreSame') {
                    return firstDigitLeft === firstDigitRight;
                }
                if (this.parameters.divMode === 'firstDigitsAreOne') {
                    const isResultEqualsTen = Math.abs(dividend / divider) === 10;
                    const isResultEqualsTenAllowed = this.examplesWithAnswerNotTenInTheRowCounter > 8;
                    return (
                        firstDigitLeft === 1 &&
                        firstDigitRight === 1 &&
                        !(isResultEqualsTen && !isResultEqualsTenAllowed)
                    );
                }
                if (firstDigitLeft === firstDigitRight) {
                    return false;
                }

                result =
                    leftCapacity === 3
                        ? checkReturnsForDividendDigitCapacityThree()
                        : checkReturnsForSelectedDigitCapacity(leftCapacity);

                return (
                    !!returnsObject[NUMBERS_FOR_DIV_MODES[divMode]] && Math.abs(dividend) / Math.abs(divider) === result
                );
            } catch (error) {
                return false;
            }
        },
        checkForRemainderInExample(left, right) {
            return left % right !== 0;
        },
        getDigitCapacity(side) {
            return this.parameters.fractions && this.parameters.fractions[side]
                ? this.parameters.digitCapacity[side][0] + +this.parameters.fractionalPart[side]
                : this.parameters.digitCapacity[side][0];
        },
        generateExample() {
            let operations = [];
            for (let key in this.parameters.operations) {
                this.parameters.operations[key] && operations.push(key);
            }
            operations[Math.floor(Math.random() * operations.length)] === 'multiply'
                ? this.generateMultiplication()
                : this.generateDivision();
        },
        generateMultiplication() {
            let left;
            let right;
            let prevExample = Object.assign(this.prevMultiplicationExample);
            do {
                if (this.parameters.numbers.length > 0) {
                    if (this.parameters.tabulatedValue) {
                        left = this.parameters.numbers[Math.floor(Math.random() * this.parameters.numbers.length)];
                        right = Math.round(Math.random() * 9) + 1;
                        if (
                            this.prevMultiplicationExample.left === left &&
                            this.prevMultiplicationExample.right === right
                        ) {
                            let newRight = Math.round(Math.random() * 8) + 1;
                            right = newRight >= right ? right + 1 : newRight;
                        }
                        this.prevMultiplicationExample = { left, right };
                    } else {
                        left = this.parameters.numbers[Math.floor(Math.random() * this.parameters.numbers.length)];
                        right = this.generateNumber('right', this.prevMultiplicationExample.right, 1, left);
                        this.prevMultiplicationExample = { left, right };
                        if (this.parameters.fractions && this.parameters.fractions.right) {
                            right /= Math.pow(10, +this.parameters.fractionalPart.right);
                        }
                    }
                } else {
                    left = this.generateNumber('left', this.prevMultiplicationExample.left);
                    right = this.generateRightNumber(left);
                    this.prevMultiplicationExample = { left, right };
                    [left, right] = this.handleFractionalPart(left, right);
                }
            } while (left * right === prevExample.left * prevExample.right);
            this.example = { left, right, operation: '*' };
        },
        generateDivision() {
            let right;
            let left;
            let dividend;
            let prevExample = Object.assign({}, this.prevDivisionExample);
            do {
                if (this.parameters.numbers.length > 0) {
                    if (this.parameters.tabulatedValue) {
                        right = this.parameters.numbers[Math.floor(Math.random() * this.parameters.numbers.length)];
                        left = right * (Math.round(Math.random() * 9) + 1);
                        if (this.prevDivisionExample.left === left && this.prevDivisionExample.right === right) {
                            let newLeft = right * (Math.round(Math.random() * 8) + 1);
                            left = newLeft >= left ? left + right : newLeft;
                        }
                        this.prevDivisionExample = { left, right };
                    } else {
                        do {
                            right = this.parameters.numbers[Math.floor(Math.random() * this.parameters.numbers.length)];
                            dividend = this.generateNumber('right', 1, right, right);
                            left = right * dividend;
                        } while (this.prevDivisionExample.left === left && right === this.prevDivisionExample.right);
                        this.prevDivisionExample = { left, right };
                        if (this.parameters.fractions && this.parameters.fractions.right) {
                            left /= Math.pow(10, +this.parameters.fractionalPart.right);
                        }
                    }
                } else {
                    let divider =
                        this.parameters.digitCapacity.right[0] === this.parameters.digitCapacity.left[0] ? 2 : 1;
                    right = this.generateNumber('right', this.prevDivisionExample.right, divider);
                    dividend = this.generateNumber('left', 1, right, right);
                    if (this.onlyNegativeAnswers && right < 0) {
                        dividend = -dividend;
                    }
                    const isRemainder = this.parameters.remainder;
                    left = right * dividend;
                    if (isRemainder) {
                        do {
                            const isLeftNumberLengthGreaterThanOrEqualToLeftCapacity =
                                Math.abs(left).toString().length >= this.parameters.digitCapacity.left[0];
                            if (this.parameters.remainder && isLeftNumberLengthGreaterThanOrEqualToLeftCapacity) {
                                const generatedNumber = random(1, Math.abs(right) - 1);
                                left < 0 ? (left += generatedNumber) : (left -= generatedNumber);
                            }
                        } while (!this.checkForRemainderInExample(left, right));
                    }

                    this.prevDivisionExample = { left, right };
                    [left, right] = this.handleFractionalPart(left, right);
                }
            } while (
                Math.abs(left / right) === Math.abs(prevExample.left / prevExample.right) ||
                (this.parameters.divMode && !this.checkDivModes(left, right))
            );
            this.examplesWithAnswerNotTenInTheRowCounter =
                Math.abs(left / right) === 10 ? 0 : this.examplesWithAnswerNotTenInTheRowCounter + 1;
            this.example = { left, right, operation: '/' };
        },
        generateNumber(side, prevNumber = null, divider = 1, leftNumber = null) {
            let digitCapacity = this.getDigitCapacity(side);
            let generatedNumber;
            let isGeneratedNumberSuitable = false;
            let allowLessCapacity =
                this.parameters.fractions &&
                this.parameters.fractions[side] &&
                digitCapacity - +this.parameters.fractionalPart[side] === 1;
            let factor = divider === 2 ? 1 : divider;
            do {
                generatedNumber = Math.floor(Math.random() * (Math.pow(10, digitCapacity) / divider));
                if (this.parameters.extraOperations?.abCrossAc && this.endsWithZero(generatedNumber)) {
                    generatedNumber += 1;
                }
                if (!this.onlyPositiveAnswers && !this.onlyNegativeAnswers) {
                    generatedNumber = Math.random() > 0.5 ? generatedNumber : -generatedNumber;
                } else if (this.onlyNegativeAnswers) {
                    if (leftNumber === null) {
                        generatedNumber = Math.random() > 0.5 ? generatedNumber : -generatedNumber;
                    } else if (leftNumber > 0) {
                        if (generatedNumber > 0) {
                            generatedNumber = -generatedNumber;
                        }
                    } else {
                        if (generatedNumber < 0) {
                            generatedNumber = -generatedNumber;
                        }
                    }
                }
                if (generatedNumber < 0 && factor < 0) {
                    isGeneratedNumberSuitable = (generatedNumber * factor).toString().length !== digitCapacity;
                } else if (generatedNumber < 0 || factor < 0) {
                    isGeneratedNumberSuitable = (generatedNumber * factor).toString().length !== digitCapacity + 1;
                } else {
                    isGeneratedNumberSuitable = (generatedNumber * factor).toString().length !== digitCapacity;
                }
            } while (
                (!allowLessCapacity && isGeneratedNumberSuitable) ||
                generatedNumber === 0 ||
                generatedNumber === 1 ||
                generatedNumber === -1 ||
                generatedNumber === prevNumber ||
                generatedNumber === -prevNumber
            );
            return generatedNumber;
        },
        handleFractionalPart(left, right) {
            if (this.parameters.fractions && this.parameters.fractions.right) {
                right /= Math.pow(10, +this.parameters.fractionalPart.right);
            }
            if (this.parameters.fractions && this.parameters.fractions.left) {
                left /= Math.pow(10, +this.parameters.fractionalPart.left);
            }
            return [left, right];
        },
        getRule101Number(left) {
            for (const operation in this.parameters.extraOperations.rule101) {
                if (RULE101_OPERATIONS.includes(operation) && this.parameters.extraOperations.rule101[operation]) {
                    if (this.onlyPositiveAnswers) {
                        return +operation;
                    }
                    if (this.onlyNegativeAnswers) {
                        const isLeftNegative = left < 0;
                        return !isLeftNegative ? -operation : +operation;
                    }
                    return this.getRandomSign() * +operation;
                }
            }
        },
        getMirrorNumber(left) {
            const digit = this.parameters.extraOperations.mirror.digits.indexOf(true) + 1;
            if (this.onlyPositiveAnswers) {
                return +digit.toString().repeat(this.parameters.digitCapacity.right[0]);
            }
            if (this.onlyNegativeAnswers) {
                const isLeftNegative = left < 0;
                return !isLeftNegative
                    ? -digit.toString().repeat(this.parameters.digitCapacity.right[0])
                    : +digit.toString().repeat(this.parameters.digitCapacity.right[0]);
            }
            return this.getRandomSign() * +digit.toString().repeat(this.parameters.digitCapacity.right[0]);
        },
        getAbCrossAcNumber(left) {
            const unsignedLeft = Math.abs(left);
            const firstDigitString = unsignedLeft.toString()[0];
            const leftNumberSecondDigit = +unsignedLeft.toString()[1];

            if (this.onlyPositiveAnswers) {
                return +(firstDigitString + (10 - leftNumberSecondDigit));
            }
            if (this.onlyNegativeAnswers) {
                const isLeftNegative = left < 0;
                return !isLeftNegative
                    ? -(firstDigitString + (10 - leftNumberSecondDigit))
                    : +(firstDigitString + (10 - leftNumberSecondDigit));
            }

            return this.getRandomSign() * +(firstDigitString + (10 - leftNumberSecondDigit));
        },
        generateRightNumber(left) {
            if (this.parameters.extraOperations?.abCrossAc) {
                return this.getAbCrossAcNumber(left);
            }
            if (this.parameters.extraOperations?.mirror?.isChosen) {
                return this.getMirrorNumber(left);
            }
            if (this.parameters.extraOperations?.rule101?.isChosen) {
                return this.getRule101Number(left);
            }
            return this.generateNumber('right', this.prevMultiplicationExample.right, 1, left);
        },
        getRandomSign() {
            return Math.random() > 0.5 ? 1 : -1;
        },
        endsWithZero(number) {
            return number % 10 === 0;
        },
    },
    computed: {
        onlyPositiveAnswers() {
            return this.parameters.positiveAnswers && !this.parameters.negativeAnswers;
        },
        onlyNegativeAnswers() {
            return this.parameters.negativeAnswers && !this.parameters.positiveAnswers;
        },
    },
};
